import type { ChangeTracker } from '../change-tracking/change-tracker';
import { BulkCommand } from '../commands/bulk.command';
import { ChangeOwnEmailAddressCommand } from '../commands/change-own-email-address.command';
import type { ICommand } from '../commands/i-command.interface';
import { ChangeOwnEmailAddressContent } from '../commands/payloads/change-own-email-address-content';
import { UpdateUserCommand } from '../commands/update-user.command';
import type { OwnProfileFormData } from '../components/_forms/own-profile-form';
import type { User } from '../domain/entities/user.entity';
import type { CommandSendPayload } from '../message-broker/payloads/command-send.payload';
import type { CommandResultBus } from './command-result-bus.interface';
import type { OwnProfile } from './own-profile.state';

/** The part of the CommandBus the save needs; kept as a name for the profile save's callers and tests. */
export type OwnProfileSaveBus = CommandResultBus;

/** What a save did: nothing to send, saved (with the new saved state), or refused by the server. */
export type OwnProfileSaveOutcome =
    | { kind: 'nothing' }
    | { kind: 'saved'; profile: OwnProfile }
    | { kind: 'refused'; error: unknown };

/** The logged-in user as the page holds it: the change-tracked proxy from `UserRepository`. */
export type TrackedUser = User & { changeTracker: ChangeTracker<User> };

/**
 * Saves the "Mein Profil" form: the changed names go onto the user proxy and out as `update.user`
 * (only the changed names, ADR-015); a changed e-mail goes out as `change.own-email-address` with the
 * current password; both together as one bulk, which the server saves completely or not at all.
 * Kept out of the element so it runs without a DOM.
 *
 * Names are compared trimmed against the trimmed proxy value, so a change of surrounding spaces is no
 * change and sends nothing.
 *
 * The change tracker is reset after every outcome, a dispatch that throws included — `reset()` has no other caller, so without it every
 * later save would resend old keys. On a refusal the proxy first gets the names of the last saved state
 * back (`saved`, not the tracker's load-time originals), then the reset, in that order: the reverse
 * would leave the restored names marked as changed.
 *
 * The CommandBus clears the password on the payload right after it built the request body
 * (`SensitivePayload`); it is cleared here once more after every outcome.
 */
export async function saveOwnProfile(
    user: TrackedUser,
    saved: OwnProfile,
    value: OwnProfileFormData,
    bus: OwnProfileSaveBus,
): Promise<OwnProfileSaveOutcome> {
    const firstName = value.firstName.trim();
    const lastName = value.lastName.trim();
    const emailAddress = value.emailAddress.trim();

    if (firstName !== user.firstName.trim()) {
        user.firstName = firstName;
    }
    if (lastName !== user.lastName.trim()) {
        user.lastName = lastName;
    }
    const commands: ICommand<unknown>[] = [];
    if (user.changeTracker.isDirty) {
        commands.push(new UpdateUserCommand(user));
    }
    let emailContent: ChangeOwnEmailAddressContent | null = null;
    if (emailAddress !== saved.emailAddress) {
        emailContent = new ChangeOwnEmailAddressContent(emailAddress, value.currentPassword);
        commands.push(new ChangeOwnEmailAddressCommand(saved.uid, emailContent));
    }
    if (commands.length === 0) {
        return { kind: 'nothing' };
    }

    const refused = (error: unknown): OwnProfileSaveOutcome => {
        user.firstName = saved.firstName;
        user.lastName = saved.lastName;
        user.changeTracker.reset();

        return { kind: 'refused', error };
    };

    try {
        const command = commands.length === 1 ? commands[0] : new BulkCommand(commands);
        let result: CommandSendPayload;
        try {
            result = await bus.dispatchCommandForResult(command);
        } catch (error) {
            // A dispatch that throws instead of answering counts as a refusal.
            return refused(error);
        }
        if (!result.success) {
            return refused(result.error);
        }

        user.changeTracker.reset();
        user.emailAddress = emailAddress;

        return { kind: 'saved', profile: { ...saved, firstName: user.firstName, lastName: user.lastName, emailAddress } };
    } finally {
        emailContent?.clearSensitiveValues();
    }
}
